import { useQuery } from "@tanstack/react-query";
import { ExternalLink } from "lucide-react";

import { adminGet } from "@lib/adminApi";

import {
  BAND_LABEL,
  BAND_STYLE,
  BAND_TEXT,
  formatAge,
  formatBytes,
  formatMetric,
  METRIC_SHORT,
  percentChange,
  psiUrl,
} from "./healthFormat";
import { ErrorLine, Panel, REFRESH_MS, RefreshFailed } from "./panelParts";
import { perfHeadline, type PerfHeadlineTone } from "./perfSummary";
import {
  PERF_METRICS,
  type PerfHostTrend,
  type PerfMetricKey,
  type PerfPoint,
  type PerfResourceRow,
  type PerfResponse,
  type PerfThreshold,
} from "./types";

const HEADLINE_STYLE: Record<PerfHeadlineTone, string> = {
  ok: "text-green-700",
  warn: "text-amber-700",
  bad: "text-red-700",
};

// Site performance from the nightly perf probe (OMG-2767 phase 2, read like
// Lighthouse since OMG-2795): one card per probed page, leading with LCP in
// seconds and the other four lab metrics, each in Google's good / needs
// improvement / poor colour, then a disclosure with the warnings and
// diagnostics behind the numbers (the probe's findings, the heaviest assets,
// what blocked rendering, third parties, image problems, page weight and its
// 30-night line). Timings are lab numbers on a throttled phone: trend them
// night to night; the PageSpeed Insights link per site has the field data.

const NIGHTS = 30;
/** Compare against the run seven points back: about a week, but a night with
 *  no summary is not a point, so the label says "runs", not "nights". The
 *  colors are a heuristic, not the probe's growth budget (which compares
 *  against the fixed baseline). */
const COMPARE_BACK = 7;

const Sparkline = ({
  points,
  label,
}: {
  points: (number | null)[];
  label: string;
}) => {
  const values = points.filter((v): v is number => v !== null);
  if (values.length < 2) return <div className="h-10" />;
  const max = Math.max(...values);
  const min = Math.min(...values);
  // The vertical range is at least 20% of the mean, centred on the data, so
  // night-to-night noise of a few percent draws as a near-flat line and only
  // a real change looks like one.
  const mean = values.reduce((a, b) => a + b, 0) / values.length;
  const span = Math.max(max - min, 0.2 * mean) || 1;
  const low = (max + min) / 2 - span / 2;
  const H = 30;
  const step = 100 / (points.length - 1);
  // A missing night breaks the line instead of drawing a false drop to zero.
  const segments: string[] = [];
  let current: string[] = [];
  points.forEach((v, i) => {
    if (v === null) {
      if (current.length) segments.push(current.join(" "));
      current = [];
      return;
    }
    const y = H - 2 - ((v - low) / span) * (H - 4);
    current.push(`${(i * step).toFixed(2)},${y.toFixed(2)}`);
  });
  if (current.length) segments.push(current.join(" "));
  return (
    <svg
      viewBox={`0 0 100 ${H}`}
      preserveAspectRatio="none"
      className="h-10 w-full text-cta"
      role="img"
      aria-label={label}
    >
      {segments.map((pts, i) => (
        <polyline
          key={i}
          points={pts}
          fill="none"
          stroke="currentColor"
          strokeWidth={1.5}
          vectorEffect="non-scaling-stroke"
        />
      ))}
    </svg>
  );
};

const changeStyle = (pct: number | null) =>
  pct === null
    ? "text-gray-400"
    : pct >= 20
      ? "text-red-700"
      : pct >= 10
        ? "text-amber-700"
        : "text-gray-500";

const Stat = ({ label, value }: { label: string; value: string }) => (
  <div>
    <div className="text-[11px] text-gray-500">{label}</div>
    <div className="text-xs font-medium">{value}</div>
  </div>
);

/** One metric chip: value in the band's colour, the band on hover. */
const MetricChip = ({
  metric,
  trend,
  threshold,
}: {
  metric: PerfMetricKey;
  trend: PerfHostTrend;
  threshold: PerfThreshold;
}) => {
  const m = trend.metrics[metric];
  const band = m.band ?? "none";
  const title =
    m.band === null
      ? `${threshold.label}: not measured`
      : `${threshold.label}: ${BAND_LABEL[m.band]} (good up to ${formatMetric(metric, threshold.good)}, poor over ${formatMetric(metric, threshold.poor)})`;
  return (
    <span
      title={title}
      className={`inline-flex items-baseline gap-1 px-1.5 py-0.5 rounded text-xs ${BAND_STYLE[band]}`}
    >
      <span className="text-[10px] uppercase tracking-wide opacity-70">
        {METRIC_SHORT[metric]}
      </span>
      <span className="font-semibold">{formatMetric(metric, m.value)}</span>
    </span>
  );
};

const ResourceList = ({
  title,
  rows,
  total,
}: {
  title: string;
  rows: PerfResourceRow[];
  total: number | null;
}) => {
  if (rows.length === 0) return null;
  const more = total !== null && total > rows.length ? total - rows.length : 0;
  return (
    <div>
      <div className="text-[11px] text-gray-500 mb-0.5">
        {title}
        {total !== null ? ` (${total})` : ""}
      </div>
      <ul className="space-y-0.5">
        {rows.map((r, i) => (
          <li
            key={`${r.url}-${i}`}
            title={r.url}
            className="text-xs font-mono truncate"
          >
            <span className="text-gray-500 w-14 inline-block">
              {formatBytes(r.bytes)}
            </span>
            <span className="text-gray-400 w-16 inline-block">{r.kind}</span>
            {r.url}
          </li>
        ))}
        {more > 0 && (
          <li className="text-xs text-gray-400">
            and {more} more in the run report
          </li>
        )}
      </ul>
    </div>
  );
};

/** The warnings and diagnostics behind a site's numbers, one click away. */
const SiteDetail = ({ trend }: { trend: PerfHostTrend }) => {
  const last: PerfPoint | undefined = trend.points[trend.points.length - 1];
  const before =
    trend.points[Math.max(0, trend.points.length - 1 - COMPARE_BACK)];
  const change =
    trend.points.length > 1
      ? percentChange(before?.nonMediaBytes, last?.nonMediaBytes)
      : null;
  const fails = trend.findings.filter(
    (f) => f.severity === "fail" || f.severity === "error",
  );
  const warns = trend.findings.filter((f) => f.severity === "warn");
  const d = trend.detail;
  const img = d?.images ?? null;
  const imageProblems = img
    ? [
        img.oversized ? `${img.oversized} oversized` : null,
        img.legacyFormat ? `${img.legacyFormat} JPEG/PNG/GIF` : null,
        img.eagerOffscreen
          ? `${img.eagerOffscreen} eager below the fold`
          : null,
      ].filter((s): s is string => s !== null)
    : [];

  return (
    <div className="mt-2 space-y-3 border-t pt-2">
      {trend.lcpElement && (
        <p className="text-xs text-gray-600">
          Largest paint: {trend.lcpElement.tag ?? "element"}
          {trend.lcpElement.src && (
            <span className="font-mono" title={trend.lcpElement.src}>
              {" "}
              {trend.lcpElement.src}
            </span>
          )}
          {trend.lcpElement.loading === "lazy" && (
            <span className="text-red-700">
              {" "}
              (lazy-loaded: should be eager)
            </span>
          )}
        </p>
      )}

      {(fails.length > 0 || warns.length > 0) && (
        <div>
          <div className="text-[11px] text-gray-500 mb-0.5">Probe findings</div>
          <ul className="space-y-0.5">
            {fails.map((f, i) => (
              <li
                key={`f${i}`}
                className={`text-xs ${f.known ? "text-gray-500" : "text-red-700"}`}
              >
                <span className="font-semibold">
                  {f.known
                    ? "Known"
                    : f.severity === "error"
                      ? "Error"
                      : "Fail"}
                </span>{" "}
                {f.message}
              </li>
            ))}
            {warns.map((w, i) => (
              <li key={`w${i}`} className="text-xs text-amber-700">
                <span className="font-semibold">Warn</span> {w.message}
              </li>
            ))}
          </ul>
        </div>
      )}

      {d && (
        <>
          <ResourceList
            title="Render-blocking"
            rows={d.renderBlocking}
            total={d.renderBlockingCount}
          />
          <ResourceList
            title="Heaviest resources"
            rows={d.heaviest}
            total={null}
          />
          {d.thirdParty.length > 0 && (
            <div>
              <div className="text-[11px] text-gray-500 mb-0.5">
                Third-party hosts
                {d.thirdPartyCount !== null ? ` (${d.thirdPartyCount})` : ""}
              </div>
              <ul className="space-y-0.5">
                {d.thirdParty.map((h) => (
                  <li key={h.host} className="text-xs font-mono truncate">
                    <span className="text-gray-500 w-14 inline-block">
                      {formatBytes(h.bytes)}
                    </span>
                    <span className="text-gray-400 w-16 inline-block">
                      {h.requests === null ? "" : `${h.requests} req`}
                    </span>
                    {h.host}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {img && (
            <p className="text-xs text-gray-600">
              Images: {img.count ?? "n/a"} ({formatBytes(img.bytes)})
              {imageProblems.length > 0 && `, ${imageProblems.join(", ")}`}
            </p>
          )}
        </>
      )}

      <div>
        <div className="flex items-baseline gap-2">
          <span className="text-[11px] text-gray-500">Page weight</span>
          <span className="text-sm font-semibold">
            {formatBytes(last?.nonMediaBytes ?? null)}
          </span>
          <span className="text-xs text-gray-500">excl. media</span>
          {change !== null && (
            <span className={`text-xs font-semibold ${changeStyle(change)}`}>
              {change > 0 ? `+${change}` : change}% vs{" "}
              {Math.min(COMPARE_BACK, trend.points.length - 1)} runs ago
            </span>
          )}
        </div>
        <Sparkline
          points={trend.points.map((p) => p.nonMediaBytes)}
          label={`${trend.label} page weight excluding media, last ${trend.points.length} nights`}
        />
        <div className="grid grid-cols-4 gap-1 mt-1">
          <Stat label="JS" value={formatBytes(last?.scriptBytes ?? null)} />
          <Stat label="Images" value={formatBytes(last?.imageBytes ?? null)} />
          <Stat label="Media" value={formatBytes(last?.mediaBytes ?? null)} />
          <Stat
            label="Requests"
            value={last?.requests == null ? "n/a" : String(last.requests)}
          />
        </div>
      </div>

      {trend.url && (
        <a
          href={psiUrl(trend.url)}
          target="_blank"
          rel="noreferrer"
          className="inline-flex items-center gap-1 text-xs text-blue-700 hover:underline"
        >
          PageSpeed Insights (field data from real Chrome users){" "}
          <ExternalLink size={10} />
        </a>
      )}
    </div>
  );
};

const SiteCard = ({
  trend,
  thresholds,
}: {
  trend: PerfHostTrend;
  thresholds: PerfResponse["thresholds"];
}) => {
  const unexcused = trend.findings.filter(
    (f) => (f.severity === "fail" || f.severity === "error") && !f.known,
  ).length;
  const lcp = trend.metrics.lcp;
  const lcpBand = lcp.band ?? "none";
  const problems =
    trend.findings.length === 0
      ? "no findings"
      : `${trend.findings.length} finding${trend.findings.length > 1 ? "s" : ""}`;

  return (
    <div
      className={`border rounded-lg p-3 ${unexcused ? "border-red-300" : ""}`}
    >
      <div className="flex items-start justify-between gap-2">
        <div className="font-medium text-sm">{trend.label}</div>
        {trend.url && (
          <a
            href={trend.url}
            target="_blank"
            rel="noreferrer"
            className="text-gray-400 hover:text-blue-700"
            aria-label={`Open ${trend.label}`}
          >
            <ExternalLink size={12} />
          </a>
        )}
      </div>
      <div className="flex items-baseline gap-2 mt-1">
        <span className={`text-2xl font-bold ${BAND_TEXT[lcpBand]}`}>
          {formatMetric("lcp", lcp.value)}
        </span>
        <span className="text-xs text-gray-500">
          LCP{lcp.band ? `, ${BAND_LABEL[lcp.band].toLowerCase()}` : ""}
        </span>
      </div>
      <div className="flex flex-wrap gap-1 mt-2">
        {PERF_METRICS.filter((m) => m !== "lcp").map((m) => (
          <MetricChip
            key={m}
            metric={m}
            trend={trend}
            threshold={thresholds[m]}
          />
        ))}
      </div>
      <details className="mt-2 group">
        <summary className="text-xs text-blue-700 cursor-pointer select-none">
          <span className="group-open:hidden">Show details</span>
          <span className="hidden group-open:inline">Hide details</span>
          <span className="text-gray-400"> · {problems}</span>
        </summary>
        <SiteDetail trend={trend} />
      </details>
    </div>
  );
};

const Legend = ({ thresholds }: { thresholds: PerfResponse["thresholds"] }) => (
  <p className="text-xs text-gray-400 mt-2">
    Nightly, Pixel 7 on throttled slow 4G, median of 3 cold loads. Bands are
    Google&apos;s: good up to{" "}
    {PERF_METRICS.map((m, i) => (
      <span key={m}>
        {i > 0 && ", "}
        {METRIC_SHORT[m]} {formatMetric(m, thresholds[m].good)}
      </span>
    ))}
    ; poor past{" "}
    {PERF_METRICS.map((m, i) => (
      <span key={m}>
        {i > 0 && ", "}
        {METRIC_SHORT[m]} {formatMetric(m, thresholds[m].poor)}
      </span>
    ))}
    . TBT stands in for INP, which needs real interactions; Speed Index is not
    measured. Byte budgets fail a run; timings are lab numbers to compare night
    to night, not PageSpeed scores. Known: a dated, excused failure that does
    not page.
  </p>
);

export const PerfPanel = () => {
  const { data, error, isLoading } = useQuery({
    queryKey: ["admin-health-perf"] as const,
    queryFn: () =>
      adminGet<PerfResponse>(`/admin/health/perf?nights=${NIGHTS}`),
    refetchInterval: REFRESH_MS,
  });
  const title = "Site performance";

  if (isLoading) return <Panel title={title}>Loading...</Panel>;
  if (!data)
    return (
      <Panel title={title}>
        <ErrorLine error={error} />
      </Panel>
    );

  if (!data.latest || data.hosts.length === 0) {
    return (
      <Panel title={title}>
        {error && <RefreshFailed error={error} />}
        <p className="text-sm text-gray-600">
          The nightly perf probe has not reported yet. It runs at 10:50 UTC and
          reports once the marketing-tracking repository has the
          MONITOR_INGEST_URL variable and the MONITOR_INGEST_KEY secret.
        </p>
      </Panel>
    );
  }

  // Deploy order: this page and the omg-api change for OMG-2795 ship
  // separately. An API without `thresholds` cannot band anything, so say so
  // instead of throwing on `thresholds[m]` and taking the page down.
  if (!data.thresholds || data.hosts.some((h) => !h.metrics)) {
    return (
      <Panel title={title}>
        {error && <RefreshFailed error={error} />}
        <p className="text-sm text-amber-700">
          omg-api is older than this page: it reports page weight but not the
          Lighthouse bands (OMG-2795). The cards return once the matching
          omg-api release is deployed.
        </p>
      </Panel>
    );
  }

  const headline = perfHeadline(data);
  const ageHours = headline.summaryAt
    ? (Date.now() - headline.summaryAt.getTime()) / 3_600_000
    : null;

  return (
    <Panel title={title}>
      {error && <RefreshFailed error={error} />}
      <p className="text-sm mb-3">
        <span className={HEADLINE_STYLE[headline.tone]}>{headline.text}</span>{" "}
        <span className="text-xs text-gray-500">
          {`Cards from ${ageHours === null ? "an unknown time" : formatAge(ageHours)} · newest run: `}
          {data.latest.runUrl ? (
            <a
              href={data.latest.runUrl}
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-0.5 text-blue-700 hover:underline"
            >
              {data.latest.conclusion.replace("_", " ")}{" "}
              <ExternalLink size={10} />
            </a>
          ) : (
            data.latest.conclusion.replace("_", " ")
          )}
        </span>
      </p>
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
        {data.hosts.map((h) => (
          <SiteCard key={h.host} trend={h} thresholds={data.thresholds} />
        ))}
      </div>
      <Legend thresholds={data.thresholds} />
    </Panel>
  );
};
